import { Component, Inject } from '@angular/core';
import {
  DwComponent,
  DwComponentSectionConfigPreference,
  DwComponentType,
  DwContainerOptions,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import {
  AppMetaDataItemNames,
  CheckRideRequestEntity,
  CheckRideRequestSubStatusEntity,
  CheckRideStatusLevelAllItems,
  CheckRideWorkflowAllItems
} from '../../../../meta-data/app-meta-data.service';
import { CheckrideStatusConfig } from '../../models/checkride-status-config.model';
import { CheckrideStatusConfigComponent } from '../checkride-status-config/checkride-status-config.component';

const CHECKRIDE_STATUS_CONTEXT_KEY = 'Checkride_Request-Status_Details';

@DwComponent({
  key: 'checkride-status',
  name: 'Checkride Status',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.CheckRideRequest,
  configComponent: CheckrideStatusConfigComponent,
  isGlobal: false
})
@Component({
  selector: 'app-checkride-status',
  templateUrl: './checkride-status.component.html',
  styleUrls: ['./checkride-status.component.scss']
})
export class CheckrideStatusComponent extends DwSectionBaseComponent<DwComponentSectionConfigPreference<CheckrideStatusConfig>> {
  formApi: DwMetaDataFormApi;
  checkRideRequest: CheckRideRequestEntity;
  containerOptions: DwContainerOptions;

  private _status: CheckRideRequestSubStatusEntity;

  get status(): CheckRideRequestSubStatusEntity {
    return this._status;
  }

  set status(status: CheckRideRequestSubStatusEntity) {
    this._status = status;
    this.updateContainerOptions();
  }

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwMetaDataFormStateService: DwMetaDataFormStateService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwMetaDataFormStateService.state.formApi;
  }

  setupComponentComplete(): void {
    this.checkRideRequest = this.formApi.getFormData<CheckRideRequestEntity>();
    this.subscriptions.push(
      this.formApi.getFormDataChanges().subscribe(checkRideRequest => {
        this.checkRideRequest = checkRideRequest;
        this.updateContainerOptions();
      })
    );

    const statusContext = this.context[CHECKRIDE_STATUS_CONTEXT_KEY];

    if (!statusContext) {
      return;
    }

    this.status = statusContext.value;
    this.subscriptions.push(statusContext.subscribe(status => this.status = status));
  }

  get title(): string {
    return this.status?.CheckRideRequestSubStatus;
  }

  get message(): string {
    if (!this.status) {
      return null;
    }

    const config = this.config;

    switch (config.audience) {
      case 'examiner':
        return this.status.ExaminerNotes;
      case 'school':
        return this.useManualSchoolStatus ? this.status.SchoolManualNotes : this.status.SchoolNotes;
      default:
        return this.status.ApplicantNotes;
    }
  }

  private get config(): CheckrideStatusConfig {
    return this.sectionConfig?.config || { audience: 'student' };
  }

  private get statusLevelId(): number {
    if (!this.status) {
      return null;
    }

    switch (this.config.audience) {
      case 'examiner':
        return this.status.ExaminerStatusLevelId;
      case 'school':
        return this.useManualSchoolStatus
          ? this.status.SchoolManualStatusLevelId
          : this.status.SchoolStatusLevelId;
      default:
        return this.status.ApplicantStatusLevelId;
    }
  }

  private get useManualSchoolStatus(): boolean {
    return this.checkRideRequest?.CheckRideWorkflowId === CheckRideWorkflowAllItems.SchoolWorkflowManualNoPilexosExaminer;
  }

  private updateContainerOptions(): void {
    const isSuccess = this.statusLevelId === CheckRideStatusLevelAllItems.AllGoodGreen;

    this.containerOptions = {
      showTitle: false,
      useContainerStyling: true,
      styleName: isSuccess ? 'CALLOUT_SUCCESS' : 'CALLOUT_WARNING'
    };
  }
}
